import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { Location } from 'react-router-dom'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { loadContactsByIds } from '@disco/state-management/actions/contact-actions'
import {
  fetchTrackMetadata,
  initEditedTrack,
} from '@disco/state-management/actions/ServerActions'
import {
  getLocationBeforeTransitions,
  getRoutingPreviousLocation,
} from '@disco/state-management/selectors/routing'
import {
  getAdjancentPlaylistTracks,
  getAdjancentShortlistTracks,
  getTrack,
} from '@disco/state-management/selectors/tracks'
import captureError from '@disco/utils/captureError'
import {
  getCommonBasicMetadataFields,
  getCommonExtraMetadataFields,
} from '@disco/utils/commonMetadata'
import { getModalBestReturnTo } from '@disco/utils/modals'

import type { EditableTrack, Track, TrackId } from 'types/track'

import { getShortlistBaseRoute } from 'utils/routing'

import DocumentHeadTags from 'components/document-head-tags'
import EditMetadataModal from 'components/modals/track/edit-metadata'

import { useDispatch, useSelector } from 'hooks/use-redux'

const getNewIndex = (
  tracks: Track[],
  offset: number,
  currentIndex: number,
): number | null | undefined => {
  if (!tracks) return null
  const offsetIndex =
    offset > 0
      ? tracks
          .slice(currentIndex + 1)
          .findIndex(track => track && !track.outside_your_territories)
      : tracks
          .slice(0, currentIndex)
          .reverse()
          .findIndex(track => track && !track.outside_your_territories)
  if (offsetIndex === -1) return null
  const newIndex =
    offset > 0
      ? currentIndex + offsetIndex + offset
      : currentIndex - offsetIndex + offset
  if (newIndex < 0 || !tracks[newIndex]) return null
  return newIndex
}

const EditMetadata = () => {
  const location = useLocation()
  const params =
    useParams<{
      trackId?: string
      trackIndex?: string
      playlistId?: string
      shortlistId?: string
    }>()

  const { trackId, trackIndex, playlistId, shortlistId } = params
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [updatedWriterIds, setUpdatedWritersIdData] = useState([])
  const previousLocation = useSelector(getRoutingPreviousLocation)
  const currentLocation = useSelector(getLocationBeforeTransitions)

  let returnTo = getModalBestReturnTo(location, previousLocation)
  const previousIsAnotherModal = (previousLocation?.pathname ?? '').includes(
    'rights-data',
  )
  if (returnTo === previousLocation && previousIsAnotherModal) {
    returnTo = {
      pathname: '/search',
      state: {},
      key: '',
      search: '',
      hash: '',
    }
  }

  const handleUpdateWriters = ids => {
    setUpdatedWritersIdData(ids)
  }

  const track: Track | null | undefined = useSelector(state =>
    getTrack(state, {
      id: parseInt(trackId),
    }),
  )
  const adjancentTracks: Track[] = useSelector(state => {
    if (playlistId) {
      return getAdjancentPlaylistTracks(state, {
        playlistId: parseInt(playlistId),
      })
    }

    if (shortlistId) {
      return getAdjancentShortlistTracks(state, {
        shortlistId,
      })
    }

    return []
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [writersLoaded, setWritersLoaded] = useState<boolean>(false)
  const fetchTrack = useCallback(
    async (trackId: TrackId) => {
      try {
        setIsLoading(true)
        await dispatch(fetchTrackMetadata(trackId))
      } catch (error) {
        console.error('fetchTrack', error)
      }

      setIsLoading(false)
    },
    [dispatch],
  )
  const fetchWriters = useCallback(
    async (writersIds: number[]) => {
      try {
        await dispatch(loadContactsByIds(writersIds))
      } catch (error) {
        captureError(error, {
          description:
            'Error in fetchWriters(). Writer not found when fetching contacts.',
          extra: { writersIds },
        })
        throw error
      }
    },
    [dispatch],
  )
  const handleInitEditedTrack = useCallback(
    (track: Track) => {
      const editedTrack: EditableTrack = {
        id: track.id,
        url: track.url,
        metadata: {
          ...getCommonBasicMetadataFields([
            {
              artwork: track.artwork_sizes?.full || track.artwork,
              name: track.name,
              album: track.album,
              artist: track.artist,
            },
          ]),
          ...getCommonExtraMetadataFields([track.metadata]),
        },
        comments: track.comments ?? [],
        track_writers: track.track_writers,
        groups: track.groups,
      }
      dispatch(initEditedTrack(editedTrack))
    },
    [dispatch],
  )
  const handleClose = useCallback(() => {
    navigate(returnTo)
  }, [navigate, returnTo])
  const currentTrack: Track | null | undefined = useMemo(() => {
    return track || adjancentTracks[parseInt(trackIndex)]
  }, [adjancentTracks, track, trackIndex])
  const writersIds = useMemo(() => {
    return currentTrack?.track_writers?.map(({ writer }) => writer)
  }, [currentTrack?.track_writers])
  useEffect(() => {
    if (currentTrack) {
      handleInitEditedTrack(currentTrack)
    }
  }, [currentTrack])
  const currentTrackId: TrackId | null | undefined =
    parseInt(trackId) || currentTrack?.id
  useEffect(() => {
    if (currentTrackId) {
      fetchTrack(currentTrackId)
    }
  }, [currentTrackId])
  useEffect(() => {
    if (writersIds?.length && !writersLoaded) {
      fetchWriters(writersIds)
      setWritersLoaded(true)
    }
  }, [writersIds])
  useEffect(() => {
    if (updatedWriterIds?.length) {
      fetchWriters(updatedWriterIds)
    }
  }, [updatedWriterIds])
  // Builds link to previous/next tracks. Returns either:
  // - *function* like <Link/> - that will redirect to new track
  // - null when there is no next track
  const getSwitchTrackHandler = useCallback(
    (offset: number) => {
      const newIndex = getNewIndex(
        adjancentTracks,
        offset,
        parseInt(trackIndex, 10),
      )
      if (newIndex === null) return null
      let link: string

      if (playlistId) {
        link = `/explorer/playlists/${playlistId}/tracks/${newIndex}/edit-metadata`
      } else if (shortlistId) {
        const baseRoute = getShortlistBaseRoute(currentLocation)
        link = `${baseRoute}/shortlists/${shortlistId}/tracks/${newIndex}/edit-metadata`
      } else {
        return null
      }

      return () => {
        dispatch(sendMixpanelEvent('Cycle through metadata edit modal'))
        navigate(link)
      }
    },
    [adjancentTracks, dispatch, playlistId, shortlistId, trackIndex],
  )

  if (!currentTrack) {
    return <div>Loading</div>
  }

  return (
    <>
      <DocumentHeadTags
        title={`Edit Metadata for ${currentTrack.name} | DISCO`}
      />
      <EditMetadataModal
        editedTrackRefreshing={isLoading}
        track={currentTrack}
        onClose={handleClose}
        onNextTrackClick={getSwitchTrackHandler(+1)}
        onPrevTrackClick={getSwitchTrackHandler(-1)}
        updateWriterIds={handleUpdateWriters}
      />
    </>
  )
}

export default EditMetadata
